import { Client as TwilioConversationsClient } from "@twilio/conversations";

export type TokenResponse = { token: string; identity: string };

const tokenEndpoint = "https://garaaz-chat-9512.twil.io/token";
const changeParticipantsEndpoint =
  "https://garaaz-chat-9512.twil.io/changeParticipants";
const party = "orca";

export class TwilioService {
  static fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    timeoutMs = 10000
  ) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: controller.signal });
    } catch (err: any) {
      if (err?.name === "AbortError") {
        throw new Error(`Request timed out after ${timeoutMs}ms`);
      }
      throw err;
    } finally {
      clearTimeout(id);
    }
  };

  static async fetchToken(userId: string): Promise<TokenResponse> {
    const maxAttempts = 3;
    let lastErr: unknown = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await this.fetchWithTimeout(tokenEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, party }),
        });

        if (!resp.ok) {
          const error = await resp.text();
          throw new Error(`Token fetch failed: ${resp.status} - ${error}`);
        }

        return await resp.json();
      } catch (err) {
        lastErr = err;
        if (attempt < maxAttempts) {
          const delayMs = 200 * Math.pow(2, attempt - 1);
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }
    }

    console.error(
      "[TwilioService] Token fetch failed after retries:",
      lastErr,
    );
    throw lastErr;
  }

  static async createConversationsClient(
    userId: string,
    options?: ConstructorParameters<typeof TwilioConversationsClient>[1]
  ): Promise<{ client: TwilioConversationsClient; identity: string }> {
    try {
      let response = await this.fetchToken(userId);

      const client = new TwilioConversationsClient(response.token, options);

      await new Promise<void>((resolve, reject) => {
        const onInit = () => {
          cleanup();
          resolve();
        };
        const onFail = ({ error }: any) => {
          console.error(
            "❌ [TwilioService] Client initialization failed:",
            error
          );
          cleanup();
          reject(error || new Error("initFailed"));
        };
        const cleanup = () => {
          client.removeListener("initialized", onInit);
          client.removeListener("initFailed", onFail);
        };

        client.on("initialized", onInit);
        client.on("initFailed", onFail);
      });

      let refreshPromise: Promise<void> | null = null;

      const refresh = async () => {
        if (refreshPromise) return refreshPromise;
      
        refreshPromise = (async () => {
          try {
            response = await this.fetchToken(userId);
            await client.updateToken(response.token);
          } catch (err) {
            console.error("Token refresh failed after retries:", err);
          } finally {
            refreshPromise = null;
          }
        })();
      
        return refreshPromise;
      };   
      
      const tokenHandler = () => refresh();
      client.on("tokenAboutToExpire", tokenHandler);
      client.on("tokenExpired", tokenHandler);
      
      const cleanupTokenListeners = () => {
        client.removeListener("tokenAboutToExpire", tokenHandler);
        client.removeListener("tokenExpired", tokenHandler);
      };

      return { client, identity: response.identity };
    } catch (error) {
      console.error("❌ [TwilioService] Failed to create client:", error);
      throw error;
    }
  }

  static async changeParticipants(
    uniqueName: string,
    oldHandlerUserId: string,
    newHandlerUserId: string,
    newOrderHandlerName?: string
  ): Promise<{
    ok: boolean;
    uniqueName: string;
    conversationSid?: string;
    removed?: boolean;
    added?: boolean;
    note?: string;
  }> {
    const payload: any = {
      uniqueName,
      oldHandlerUserId,
      newHandlerUserId,
      newOrderHandlerName: newOrderHandlerName || "Order Handler",
    };

    const resp = await this.fetchWithTimeout(changeParticipantsEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      const error = await resp.text();
      console.error(
        "❌ [TwilioService] Change participants failed:",
        resp.status,
        error
      );
      throw new Error(`Change participants failed: ${resp.status} - ${error}`);
    }

    const result = await resp.json();
    return result;
  }
}
